import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useApiClient } from 'src/hooks/useApiClient';
import type * as MT from 'src/motornet/types';
import { FRESH_VEHICLES_QUERY } from './queryOptions';
import {
	DEFAULT_PAGE_SIZE,
	EMPTY_FILTER_STATE,
	PagingState,
	RoleScope,
	toSortBy,
	toVehiclesRequest,
	VehicleFilterState,
} from './params';

interface Options {
	/** Distingue la cache fra le pagine che condividono l'endpoint. */
	cacheKey: string;
	scope?: RoleScope;
	/** Parametri fissi della pagina: `scope=my-stock`, `promo=true`, ... */
	baseParams?: Partial<MT.PlatformVehiclesRequest>;
	pageSize?: number;
}

export const usePaginatedVehicles = ({
	cacheKey,
	scope = {},
	baseParams,
	pageSize = DEFAULT_PAGE_SIZE,
}: Options) => {
	const { apiClient } = useApiClient();
	const [paging, setPaging] = useState<PagingState>({ page: 1, pageSize });
	const [filters, setFilters] =
		useState<VehicleFilterState>(EMPTY_FILTER_STATE);

	const scopeKey = JSON.stringify(scope);
	const baseKey = JSON.stringify(baseParams ?? null);

	const request = useMemo(() => {
		const base = toVehiclesRequest(filters, scope, paging);
		return base ? { ...base, ...baseParams } : null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, paging, scopeKey, baseKey]);

	const { data, isPending, isFetching, error } = useQuery({
		queryKey: ['platform', 'vehicles', cacheKey, request],
		queryFn: () =>
			apiClient.motornetControllerService.getPlatformVehicles(request!),
		enabled: request !== null,
		placeholderData: keepPreviousData,
		...FRESH_VEHICLES_QUERY,
	});

	const onFiltersChange = useCallback((next: VehicleFilterState) => {
		setFilters(next);
		setPaging((prev) => ({ ...prev, page: 1 }));
	}, []);

	const onPageChange = useCallback((pageIndex: number) => {
		setPaging((prev) => ({ ...prev, page: pageIndex + 1 }));
	}, []);

	const onPageSizeChange = useCallback((size: number) => {
		setPaging((prev) => ({ ...prev, pageSize: size, page: 1 }));
	}, []);

	const onSortChange = useCallback(
		(sortBy: { id: string; desc: boolean }[]) => {
			const [first] = sortBy ?? [];
			const field = toSortBy(first?.id);

			setPaging((prev) => ({
				...prev,
				page: 1,
				sortBy: field,
				sortDirection: field ? (first.desc ? 'desc' : 'asc') : undefined,
			}));
		},
		[]
	);

	const total = data?.total ?? 0;

	return {
		request,
		items: data?.items ?? [],
		total,
		loading: request !== null && (isPending || isFetching),
		error,
		paging,
		filters,
		tableProps: {
			totalCount: total,
			pageIndex: paging.page - 1,
			pageSize: paging.pageSize,
			onPageChange,
			onPageSizeChange,
			onSortChange,
			onFiltersChange,
			filterIntialState: EMPTY_FILTER_STATE,
		},
	};
};
